/**
 * Estimation Summary helpers — app layer totals for the summary card.
 * Office Add-ins uses deliverable labels (Add-ins, Admin Panel, UI/UX, QA),
 * not web-style Frontend / Backend/API layers.
 */

export const OFFICE_ADDINS_DELIVERABLE_ORDER = ['Add-ins', 'Admin Panel', 'UI/UX'];

const OFFICE_ADDINS_INTERNAL_LAYERS = {
  'Add-ins': ['frontend', 'core_and_admin_api'],
  'Admin Panel': ['admin_panel_frontend', 'admin_panel_backend'],
  'UI/UX': ['ui_ux'],
};

export function isOfficeAddinsPlatform(platform) {
  const p = String(platform || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');
  return (
    p === 'office addins development' ||
    p === 'office add ins development' ||
    p.startsWith('office addin')
  );
}

export function isPowerAppsPlatform(platform) {
  return /^power\s*apps$/i.test(String(platform || '').trim());
}

export function isWebsitePlatform(platform) {
  const p = String(platform || '').trim().toLowerCase();
  return p.includes('website') || p.includes('web application') || p === 'web_app';
}

// Feature names that represent API / backend work (not HTML / markup UI).
// HTML must contain only frontend markup, so any backend-signalling name is
// excluded even without the literal "api" token (backend, server-side,
// microservice, CRUD, integration, ...).
const API_FEATURE_RX =
  /\b(apis?|endpoints?|webhooks?|back[- ]?end|server[- ]?side|micro[- ]?services?|web[- ]?services?|crud|integrations?|graphql)\b/i;

/** Split a frontend-type layer {module: [features]} into { html, api } hours. */
function splitLayerHtmlApi(estimationValue, layerKey) {
  const modules = estimationValue?.[layerKey];
  let html = 0;
  let api = 0;
  if (!modules || typeof modules !== 'object') return { html, api };
  for (const features of Object.values(modules)) {
    if (!Array.isArray(features)) continue;
    for (const f of features) {
      const h = Number(f?.estimated_hours) || 0;
      if (h <= 0) continue;
      if (API_FEATURE_RX.test(String(f?.feature_name || ''))) api += h;
      else html += h;
    }
  }
  return { html, api };
}

export function appendTotalHoursRow(appLayerTotals) {
  if (!Array.isArray(appLayerTotals) || appLayerTotals.length === 0) {
    return [];
  }

  const totalsWithoutGrandTotal = appLayerTotals.filter(
    (item) => String(item?.label || '').trim().toLowerCase() !== 'total hours'
  );
  const totalHours = totalsWithoutGrandTotal.reduce(
    (sum, item) => sum + (Number(item?.hours) || 0),
    0
  );
  const qaIndex = totalsWithoutGrandTotal.findIndex(
    (item) => String(item?.label || '').trim().toLowerCase() === 'qa'
  );

  if (qaIndex === -1) {
    return [...totalsWithoutGrandTotal, { label: 'Total Hours', hours: totalHours }];
  }

  return [
    ...totalsWithoutGrandTotal.slice(0, qaIndex + 1),
    { label: 'Total Hours', hours: totalHours },
    ...totalsWithoutGrandTotal.slice(qaIndex + 1),
  ];
}

/** Prefer backend estimation_summary rows when present. */
export function getTotalsFromEstimationSummary(estimationSummary) {
  if (!Array.isArray(estimationSummary) || estimationSummary.length === 0) {
    return null;
  }
  const appLayerTotals = estimationSummary
    .map((row) => ({
      label: String(row?.label ?? '').trim(),
      hours: Math.round(Number(row?.hours) || 0),
    }))
    .filter((item) => item.label);
  if (appLayerTotals.length === 0) return null;
  return { appLayerTotals: appendTotalHoursRow(appLayerTotals), moduleTotals: [] };
}

/** Office Add-ins: use deliverable keys from total_hours_dict. */
export function getTotalsFromTotalHoursDict(totalHoursDict, platform) {
  if (!totalHoursDict || typeof totalHoursDict !== 'object') return null;
  if (!isOfficeAddinsPlatform(platform)) return null;

  const keys = [...OFFICE_ADDINS_DELIVERABLE_ORDER, 'QA'];
  const appLayerTotals = keys
    .filter((key) => Number(totalHoursDict[key]) > 0)
    .map((key) => ({
      label: key,
      hours: Math.round(Number(totalHoursDict[key])),
    }));

  if (appLayerTotals.length === 0) return null;
  return { appLayerTotals: appendTotalHoursRow(appLayerTotals), moduleTotals: [] };
}

function sumLayerModules(estimationValue, layerKey, moduleTotalsMap, deliverableLabel) {
  const modules = estimationValue[layerKey];
  if (!modules || typeof modules !== 'object') return 0;

  let layerTotal = 0;
  for (const [moduleName, features] of Object.entries(modules)) {
    if (!Array.isArray(features)) continue;
    const moduleHours = features.reduce((sum, f) => sum + (Number(f?.estimated_hours) || 0), 0);
    layerTotal += moduleHours;
    const displayName =
      moduleName === '' && layerKey === 'ui_ux' ? 'UI/UX' : moduleName || deliverableLabel;
    moduleTotalsMap[displayName] = (moduleTotalsMap[displayName] || 0) + moduleHours;
  }
  return layerTotal;
}

function getOfficeAddinsTotalsFromEstimationValue(estimationValue, qaHoursFromApi) {
  const appLayerTotals = [];
  const moduleTotalsMap = {};
  let deliverySum = 0;

  const hasDeliverableKeys = OFFICE_ADDINS_DELIVERABLE_ORDER.some(
    (key) => estimationValue[key] && typeof estimationValue[key] === 'object'
  );

  if (hasDeliverableKeys) {
    for (const deliverable of OFFICE_ADDINS_DELIVERABLE_ORDER) {
      const layerTotal = sumLayerModules(
        estimationValue,
        deliverable,
        moduleTotalsMap,
        deliverable
      );
      if (layerTotal > 0) {
        appLayerTotals.push({ label: deliverable, hours: layerTotal });
        deliverySum += layerTotal;
      }
    }
  } else {
    let addInsTotal = 0;
    for (const layerKey of OFFICE_ADDINS_INTERNAL_LAYERS['Add-ins']) {
      addInsTotal += sumLayerModules(estimationValue, layerKey, moduleTotalsMap, 'Add-ins');
    }
    if (addInsTotal > 0) {
      appLayerTotals.push({ label: 'Add-ins', hours: addInsTotal });
      deliverySum += addInsTotal;
    }

    let adminTotal = 0;
    for (const layerKey of OFFICE_ADDINS_INTERNAL_LAYERS['Admin Panel']) {
      adminTotal += sumLayerModules(estimationValue, layerKey, moduleTotalsMap, 'Admin Panel');
    }
    if (adminTotal > 0) {
      appLayerTotals.push({ label: 'Admin Panel', hours: adminTotal });
      deliverySum += adminTotal;
    }

    const uiuxTotal = sumLayerModules(estimationValue, 'ui_ux', moduleTotalsMap, 'UI/UX');
    if (uiuxTotal > 0) {
      appLayerTotals.push({ label: 'UI/UX', hours: uiuxTotal });
      deliverySum += uiuxTotal;
    }
  }

  const qaHours =
    qaHoursFromApi != null && typeof qaHoursFromApi === 'number'
      ? Math.round(qaHoursFromApi)
      : Math.round(deliverySum * 0.1);

  if (qaHours > 0) {
    appLayerTotals.push({ label: 'QA', hours: qaHours });
  }

  const moduleTotals = Object.entries(moduleTotalsMap).map(([name, hours]) => ({
    name,
    hours,
  }));

  return { appLayerTotals: appendTotalHoursRow(appLayerTotals), moduleTotals };
}

/**
 * Compute app layer totals from estimation_value.
 * Power Apps: database + ui_ux. Office Add-ins: Add-ins / Admin Panel / UI/UX. Others: standard web layers.
 */
export function getTotalsFromEstimationValue(estimationValue, platform = '', qaHoursFromApi = null) {
  if (!estimationValue || typeof estimationValue !== 'object') {
    return { appLayerTotals: [], moduleTotals: [] };
  }

  if (isOfficeAddinsPlatform(platform)) {
    return getOfficeAddinsTotalsFromEstimationValue(estimationValue, qaHoursFromApi);
  }

  const isPowerApps = isPowerAppsPlatform(platform);
  const layerOrder = isPowerApps
    ? ['database', 'ui_ux']
    : ['frontend', 'core_and_admin_api', 'admin_panel_frontend', 'ui_ux'];
  const layerLabels = isPowerApps
    ? { database: 'Development', ui_ux: 'UI/UX' }
    : {
        frontend: platform ? `Frontend (${platform})` : 'Frontend',
        core_and_admin_api: 'Backend/API',
        admin_panel_frontend: 'Admin Panel',
        ui_ux: 'UI/UX',
      };

  const appLayerSummaryLabel = (layerKey) =>
    isPowerApps && layerKey === 'database'
      ? 'Development (Power Apps)'
      : layerLabels[layerKey];

  // HTML (markup) carved out of frontend-type layers, per platform rules:
  //   - Website / Web App: frontend + admin-panel markup, always.
  //   - Other platforms: admin-panel markup only, when an admin panel exists.
  //   (Power Apps and Office Add-ins are handled elsewhere.)
  const frontendSplit = splitLayerHtmlApi(estimationValue, 'frontend');
  const adminSplit = splitLayerHtmlApi(estimationValue, 'admin_panel_frontend');
  const hasAdminPanel = adminSplit.html + adminSplit.api > 0;
  let frontendHtml = 0;
  let adminHtml = 0;
  if (!isPowerApps) {
    if (isWebsitePlatform(platform)) {
      frontendHtml = frontendSplit.html;
      adminHtml = adminSplit.html;
    } else if (hasAdminPanel) {
      adminHtml = adminSplit.html;
    }
  }
  const htmlTotal = frontendHtml + adminHtml;

  const appLayerTotals = [];
  const moduleTotalsMap = {};
  const layerTotalsMap = {};

  for (const layerKey of layerOrder) {
    const layerTotal = sumLayerModules(estimationValue, layerKey, moduleTotalsMap, layerLabels[layerKey]);
    layerTotalsMap[layerKey] = layerTotal;
    let displayTotal = layerTotal;
    if (layerKey === 'frontend') displayTotal -= frontendHtml;
    else if (layerKey === 'admin_panel_frontend') displayTotal -= adminHtml;
    if (displayTotal > 0) {
      appLayerTotals.push({ label: appLayerSummaryLabel(layerKey), hours: displayTotal });
    }
    if (layerKey === 'frontend' && htmlTotal > 0) {
      appLayerTotals.push({ label: 'HTML', hours: htmlTotal });
    }
  }

  const qaBaseKeys = layerOrder;
  const deliverySum = qaBaseKeys.reduce((s, k) => s + (layerTotalsMap[k] ?? 0), 0);
  const qaHours =
    qaHoursFromApi != null && typeof qaHoursFromApi === 'number'
      ? Math.round(qaHoursFromApi)
      : Math.round(deliverySum * (isPowerApps ? 0.15 : 0.15));

  if (qaHours > 0) {
    appLayerTotals.push({ label: 'QA', hours: qaHours });
  }

  const moduleTotals = Object.entries(moduleTotalsMap).map(([name, hours]) => ({ name, hours }));
  return { appLayerTotals: appendTotalHoursRow(appLayerTotals), moduleTotals };
}

/** Resolve summary totals: API rows → total_hours_dict → estimation_value. */
export function resolveEstimationSummaryTotals({
  estimationSummary,
  totalHoursDict,
  estimationValue,
  platform = '',
  qaHoursFromApi = null,
  fallbackContent = '',
  parseFallback = null,
}) {
  const fromSummary = getTotalsFromEstimationSummary(estimationSummary);
  if (fromSummary) return fromSummary;

  const fromDict = getTotalsFromTotalHoursDict(totalHoursDict, platform);
  if (fromDict) return fromDict;

  if (estimationValue) {
    return getTotalsFromEstimationValue(estimationValue, platform, qaHoursFromApi);
  }

  if (typeof parseFallback === 'function') {
    return parseFallback(fallbackContent);
  }

  return { appLayerTotals: [], moduleTotals: [] };
}
